import React, { useMemo, useState } from "react";
import { QRCodeSVG } from "qrcode.react";

/* ============================================================
   DATOS
============================================================ */

const PET = {
  name: "Vulpix",
  lastName: "Castañeda Ughetti",
  fullName: "Vulpix Castañeda Ughetti",
  birth: "18 de septiembre de 2021",
  breed: "Border Collie",
  sex: "Hembra",
  eyes: "Azules",
  color: "Blanco y negro · Color tradicional",
  chip: "900263003954047",
  vaccines: "10 de abril de 2026",
  sterilized: "Sí",
  deworming: "Al día",
  city: "Medellín, Antioquia",

  responsible: {
    name: "Jenny Ughetti Valencia",
    shortName: "Jenny",
    phones: ["3007503796", "3244967629"],
  },

  emergency: {
    name: "Andres Castañeda Esguerra",
    shortName: "Andrés",
    phones: ["3234000710", "3114044203"],
  },

  personality: [
    {
      icon: "😰",
      title: "Muy nerviosa",
      text: "Necesito tiempo para sentirme segura en lugares nuevos y con personas desconocidas.",
    },
    {
      icon: "🏃‍♀️",
      title: "Escapista",
      text: "Tengo tendencia a escapar si encuentro una oportunidad. Hay que tener especial cuidado con puertas, rejas y portones.",
    },
    {
      icon: "🏠",
      title: "Me escondo",
      text: "Si no me siento segura puedo buscar un lugar para esconderme. No me fuerces, por favor.",
    },
    {
      icon: "👑",
      title: "Una princesa",
      text: "Me encanta que me consientan, me hablen bonito y me traten con amor y respeto.",
    },
  ],

  likes: [
    { icon: "🎾", title: "Perseguir pelotas", text: "¡Puedo pasar horas persiguiendo mi pelota favorita!" },
    { icon: "🥏", title: "Fanática del frisbee", text: "Me vuelve loca jugar con el disco volador." },
    { icon: "🏃‍♀️", title: "Correr", text: "Tengo mucha energía y me encanta correr." },
    { icon: "💧", title: "Agua limpia", text: "Disfruto tener siempre agua fresca y limpia." },
  ],

  avoid: [
    { icon: "🟤", title: "El barro", text: "Evito ensuciarme, especialmente con barro." },
    { icon: "💧", title: "Agua sucia", text: "Necesito que mi agua esté siempre limpia." },
    { icon: "⚠️", title: "Situaciones inseguras", text: "Los ruidos fuertes, gritos o movimientos bruscos me pueden asustar." },
    { icon: "🐕", title: "Interacción con machos", text: "Me genera estrés estar cerca de perros machos." },
  ],

  calm: [
    { icon: "🗣️", title: "Háblame con calma", text: "Usa un tono suave y tranquilo." },
    { icon: "🤲", title: "No me obligues", text: "Dame espacio y tiempo para acercarme a ti." },
    { icon: "🏠", title: "Déjame un lugar seguro", text: "Permite que pueda esconderme si lo necesito." },
    { icon: "🐕", title: "Evita movimientos bruscos", text: "No me persigas ni me acorrales." },
    { icon: "⏰", title: "Ten paciencia conmigo", text: "Puedo tardar en confiar, pero lo haré." },
  ],

  meals: [
    { time: "7:00 a. m.", label: "Primera comida" },
    { time: "2:00 p. m.", label: "Segunda comida" },
  ],

  feeding: [
    "2 cucharadas grandes de comida natural.",
    "Remojarlas en una taza de agua caliente.",
    "Agregar una ración medida de comida de grano.",
    "Mezclar según la preparación habitual de la familia.",
  ],

  routine: {
    morning: [
      "🍽️ 7:00 a. m. · Alimentación",
      "🦮 Paseo",
      "💧 Agua fresca",
      "🎾 Juego · pelota / frisbee",
      "😴 Descanso",
    ],
    afternoon: [
      "🍽️ 2:00 p. m. · Alimentación",
      "🦮 Paseo",
      "🎾 Juego · correr / perseguir",
      "💧 Agua fresca",
      "😴 Descanso",
    ],
    night: [
      "🦮 Paseo",
      "🎾 Juego tranquilo",
      "💧 Agua fresca",
      "❤️ Relajación",
      "😴 Descanso nocturno",
    ],
  },
};

const PHOTO_URL =
  "https://zaphy.com.co/wp-content/uploads/2026/08/WhatsApp-Image-2026-08-10-at-8.03.22-PM.jpeg";

/* ============================================================
   CSS
============================================================ */

const CSS = `
:root{
  --vx-navy:#082C4C;
  --vx-navy-light:#123D64;
  --vx-lime:#DCE900;
  --vx-lime-soft:#F3F7A7;
  --vx-text:#172433;
  --vx-muted:#687583;
  --vx-white:#fff;
  --vx-bg:#fff;
  --vx-soft:#F8F8F4;
  --vx-cream:#FFFDF6;
  --vx-line:#E5E8E2;
  --vx-blue:#EFF7FD;
  --vx-red:#FFF1F1;
  --vx-yellow:#FFF9DC;
  --vx-shadow:0 10px 30px rgba(8,44,76,.10);
}

*{box-sizing:border-box}
html{scroll-behavior:smooth}

.vx-app{
  width:100%;
  min-height:100vh;
  margin:0;
  padding:0;
  background:var(--vx-bg);
  color:var(--vx-text);
  font-family:Inter,Arial,Helvetica,sans-serif;
}

.vx-app button,.vx-app a{font:inherit}
.vx-app button{cursor:pointer}
.vx-app a{text-decoration:none}

/* HEADER */

.vx-top{
  width:100%;
  height:76px;
  position:sticky;
  top:0;
  z-index:999;
  display:flex;
  align-items:center;
  justify-content:space-between;
  gap:20px;
  padding:0 32px;
  background:var(--vx-navy);
  box-shadow:0 4px 20px rgba(0,0,0,.16);
}

.vx-brand{
  flex-shrink:0;
  display:flex;
  align-items:center;
  gap:11px;
  border:0;
  padding:0;
  background:transparent;
  color:white;
}

.vx-brand-paw{
  width:42px;
  height:42px;
  display:grid;
  place-items:center;
  color:var(--vx-lime);
  font-size:31px;
}

.vx-brand-text{text-align:left}

.vx-brand-text strong{
  display:block;
  color:white;
  font-size:20px;
  font-weight:900;
  letter-spacing:1px;
  line-height:1;
}

.vx-brand-text small{
  display:block;
  margin-top:4px;
  color:var(--vx-lime);
  font-size:10px;
  font-weight:700;
}

.vx-nav{
  display:flex;
  align-items:stretch;
  gap:2px;
  height:76px;
}

.vx-nav button{
  min-width:76px;
  display:flex;
  flex-direction:column;
  align-items:center;
  justify-content:center;
  gap:5px;
  padding:8px 10px;
  border:0;
  border-bottom:3px solid transparent;
  background:transparent;
  color:white;
  font-size:10px;
  font-weight:900;
  transition:.2s ease;
}

.vx-nav button .icon{font-size:19px;line-height:1}

.vx-nav button:hover,
.vx-nav button.active{
  color:var(--vx-lime);
  border-bottom-color:var(--vx-lime);
  background:rgba(255,255,255,.04);
}

/* HERO */

.vx-hero{
  position:relative;
  width:100%;
  min-height:505px;
  display:grid;
  grid-template-columns:1.08fr .92fr;
  align-items:center;
  overflow:hidden;
  background:linear-gradient(90deg,rgba(8,44,76,.98) 0%,rgba(8,44,76,.92) 42%,rgba(8,44,76,.55) 100%);
}

.vx-hero-content{
  position:relative;
  z-index:4;
  padding:60px clamp(30px,6vw,90px);
}

.vx-hero-photo-wrap{
  position:absolute;
  right:0;
  top:0;
  bottom:0;
  width:58%;
  z-index:1;
}

.vx-hero-photo{
  width:100%;
  height:100%;
  object-fit:cover;
  object-position:center;
}

.vx-hero-photo-wrap::after{
  content:"";
  position:absolute;
  inset:0;
  background:linear-gradient(90deg,var(--vx-navy) 0%,rgba(8,44,76,.65) 20%,rgba(8,44,76,.08) 70%);
}

.vx-passport-pill{
  display:inline-flex;
  align-items:center;
  gap:8px;
  padding:10px 18px;
  border-radius:999px;
  background:var(--vx-lime);
  color:var(--vx-navy);
  font-size:13px;
  font-weight:900;
}

.vx-hero h1{
  position:relative;
  margin:17px 0 0;
  color:white;
  font-size:clamp(60px,8vw,100px);
  font-weight:950;
  letter-spacing:3px;
  line-height:.95;
  text-shadow:0 5px 20px rgba(0,0,0,.2);
}

.vx-hero h2{
  margin:8px 0 0;
  color:var(--vx-lime);
  font-size:clamp(25px,3vw,40px);
  font-style:italic;
  font-weight:500;
}

.vx-hero-message{
  max-width:480px;
  margin:25px 0 0;
  color:white;
  font-size:18px;
  line-height:1.55;
}

.vx-hero-tags{
  display:flex;
  flex-wrap:wrap;
  gap:9px;
  margin-top:25px;
}

.vx-hero-tags span{
  padding:9px 12px;
  border:1px solid rgba(255,255,255,.25);
  border-radius:999px;
  background:rgba(255,255,255,.1);
  color:white;
  font-size:12px;
  font-weight:700;
}

/* IDENTIFICACIÓN */

.vx-identification{
  position:relative;
  z-index:10;
  width:min(1120px,calc(100% - 40px));
  display:grid;
  grid-template-columns:repeat(5,1fr);
  margin:-34px auto 0;
  overflow:hidden;
  border:1px solid var(--vx-line);
  border-radius:21px;
  background:white;
  box-shadow:var(--vx-shadow);
}

.vx-id-item{
  min-height:115px;
  display:flex;
  flex-direction:column;
  align-items:center;
  justify-content:center;
  padding:14px;
  text-align:center;
  border-right:1px solid var(--vx-line);
}

.vx-id-item:last-child{border-right:0}
.vx-id-icon{font-size:27px}

.vx-id-item small{
  margin-top:7px;
  color:var(--vx-navy);
  font-size:12px;
  font-weight:900;
}

.vx-id-item strong{
  margin-top:6px;
  color:var(--vx-text);
  font-size:13px;
  font-weight:500;
  line-height:1.35;
}

/* CONTENEDOR */

.vx-container{
  width:min(1120px,calc(100% - 40px));
  margin:auto;
}

.vx-home{padding:46px 0 70px}

.vx-section-heading{margin:0 0 22px}

.vx-section-heading h2{
  display:flex;
  align-items:center;
  gap:10px;
  margin:0;
  color:var(--vx-navy);
  font-size:clamp(25px,3vw,34px);
}

.vx-section-heading p{
  margin:6px 0 0;
  color:var(--vx-muted);
  font-size:14px;
}

/* TARJETAS */

.vx-feature-grid{
  display:grid;
  grid-template-columns:repeat(3,1fr);
  gap:15px;
}

.vx-feature-card{
  min-height:215px;
  display:flex;
  flex-direction:column;
  align-items:center;
  padding:23px 19px;
  border:1px solid var(--vx-line);
  border-radius:20px;
  background:white;
  text-align:center;
  box-shadow:0 5px 17px rgba(8,44,76,.05);
  transition:.2s ease;
}

.vx-feature-card:hover{
  transform:translateY(-4px);
  box-shadow:var(--vx-shadow);
}

.vx-feature-icon{
  width:66px;
  height:66px;
  display:grid;
  place-items:center;
  border-radius:50%;
  background:linear-gradient(145deg,var(--vx-lime-soft),#FFF5D5);
  font-size:30px;
}

.vx-feature-card h3{
  margin:14px 0 7px;
  color:var(--vx-navy);
  font-size:18px;
}

.vx-feature-card p{
  flex:1;
  margin:0;
  color:var(--vx-muted);
  font-size:13px;
  line-height:1.5;
}

.vx-feature-card b{
  display:inline-block;
  margin-top:17px;
  padding:8px 22px;
  border-radius:8px;
  background:var(--vx-lime);
  color:var(--vx-navy);
  font-size:12px;
}

/* ME ENCONTRASTE */

.vx-found{
  display:grid;
  grid-template-columns:140px 1fr auto;
  align-items:center;
  gap:25px;
  margin-top:24px;
  padding:22px 28px;
  border-radius:24px;
  background:linear-gradient(100deg,var(--vx-navy),#06426D);
  color:white;
}

.vx-found-photo{
  width:120px;
  height:120px;
  object-fit:cover;
  border:5px solid white;
  border-radius:50%;
  box-shadow:0 8px 30px rgba(0,0,0,.25);
}

.vx-found-content h2{
  margin:0 0 6px;
  color:white;
  font-size:28px;
}

.vx-found-content h2 span{color:var(--vx-lime)}

.vx-found-content p{
  max-width:480px;
  margin:0;
  color:rgba(255,255,255,.9);
  font-size:14px;
  line-height:1.55;
}

.vx-found-buttons{
  display:grid;
  grid-template-columns:1fr 1fr;
  gap:10px;
}

.vx-found-button{
  min-width:190px;
  padding:15px 18px;
  border-radius:12px;
  text-align:center;
  font-size:12px;
  font-weight:900;
}

.vx-found-button.primary{
  background:var(--vx-lime);
  color:var(--vx-navy);
}

.vx-found-button.secondary{
  border:1px solid var(--vx-lime);
  background:transparent;
  color:white;
}

.vx-found-phone{
  display:block;
  margin-top:5px;
  font-size:15px;
  font-weight:900;
}

/* RESUMEN */

.vx-summary-grid{
  display:grid;
  grid-template-columns:repeat(3,1fr);
  gap:16px;
  margin-top:22px;
}

.vx-summary-card{
  padding:22px;
  border:1px solid var(--vx-line);
  border-radius:20px;
  background:white;
}

.vx-summary-card h3{
  margin:0 0 14px;
  color:var(--vx-navy);
  font-size:19px;
}

.vx-summary-card>p{
  margin:0 0 16px;
  color:var(--vx-muted);
  font-size:13px;
  line-height:1.55;
}

.vx-mini-list{display:grid;gap:9px}

.vx-mini-row{
  display:flex;
  justify-content:space-between;
  gap:15px;
  padding:9px 0;
  border-bottom:1px solid var(--vx-line);
  color:var(--vx-muted);
  font-size:12px;
}

.vx-mini-row:last-child{border-bottom:0}

.vx-mini-row strong{
  color:var(--vx-navy);
  text-align:right;
}

.vx-qr-home{
  display:flex;
  flex-direction:column;
  align-items:center;
  text-align:center;
}

.vx-qr-box{
  margin-top:4px;
  padding:10px;
  border:1px solid var(--vx-line);
  border-radius:15px;
  background:white;
}

.vx-quote{
  display:flex;
  justify-content:center;
  align-items:center;
  gap:18px;
  margin-top:22px;
  padding:22px 28px;
  border-radius:20px;
  background:linear-gradient(90deg,#FFFDF6,#F7F4E8);
  color:var(--vx-navy);
  text-align:center;
}

.vx-quote-paw{
  width:60px;
  height:60px;
  display:grid;
  place-items:center;
  border-radius:50%;
  background:var(--vx-lime);
  font-size:28px;
}

.vx-quote em{display:block;font-size:20px}
.vx-quote strong{display:block;margin-top:3px;font-size:18px}

/* PÁGINAS INTERNAS */

.vx-page{
  width:min(1120px,calc(100% - 40px));
  min-height:70vh;
  margin:auto;
  padding:35px 0 80px;
}

.vx-back{
  margin-bottom:25px;
  padding:9px 15px;
  border:1px solid var(--vx-line);
  border-radius:999px;
  background:white;
  color:var(--vx-navy);
  font-size:12px;
  font-weight:800;
}

.vx-page-title{
  display:flex;
  align-items:center;
  gap:17px;
  margin-bottom:28px;
}

.vx-page-title-icon{
  width:62px;
  height:62px;
  display:grid;
  place-items:center;
  flex-shrink:0;
  border-radius:50%;
  background:linear-gradient(145deg,#FFF1C7,var(--vx-lime-soft));
  font-size:30px;
}

.vx-page-title h1{
  margin:0 0 6px;
  color:var(--vx-navy);
  font-size:clamp(30px,4vw,44px);
}

.vx-page-title p{
  margin:0;
  color:var(--vx-muted);
  font-size:14px;
}

.vx-subtitle{
  margin:28px 0 15px;
  color:var(--vx-navy);
  font-size:20px;
}

.vx-grid-2{
  display:grid;
  grid-template-columns:repeat(2,1fr);
  gap:17px;
}

.vx-box{
  padding:21px;
  border:1px solid var(--vx-line);
  border-radius:20px;
  background:white;
}

.vx-box h3{
  margin:0 0 13px;
  color:var(--vx-navy);
}

.vx-box p{
  margin:7px 0;
  color:var(--vx-muted);
  font-size:13px;
  line-height:1.55;
}

/* PERFIL */

.vx-personality{
  display:grid;
  grid-template-columns:repeat(4,1fr);
  gap:15px;
}

.vx-personality-card{
  padding:22px 19px;
  border:1px solid var(--vx-line);
  border-radius:20px;
  background:var(--vx-cream);
  text-align:center;
}

.vx-personality-card .icon{font-size:42px}

.vx-personality-card h3{
  margin:14px 0 7px;
  color:var(--vx-navy);
  font-size:16px;
}

.vx-personality-card p{
  margin:0;
  color:var(--vx-muted);
  font-size:13px;
  line-height:1.55;
}

.vx-profile-lists{
  display:grid;
  grid-template-columns:1fr 1fr;
  gap:18px;
  margin-top:22px;
}

.vx-list-box{
  padding:22px;
  border-radius:20px;
}

.vx-list-box.likes{
  background:#F7FAF0;
  border:1px solid #DDE8B8;
}

.vx-list-box.avoid{
  background:#FFF5F5;
  border:1px solid #F0CECE;
}

.vx-list-box h3{
  margin:0 0 14px;
  color:var(--vx-navy);
}

.vx-list-row{
  display:flex;
  align-items:center;
  gap:13px;
  padding:12px 0;
  border-bottom:1px solid rgba(8,44,76,.08);
}

.vx-list-row:last-child{border-bottom:0}

.vx-list-row-icon{
  width:42px;
  height:42px;
  display:grid;
  place-items:center;
  flex-shrink:0;
  border-radius:50%;
  background:rgba(220,233,0,.18);
  font-size:21px;
}

.vx-list-row strong{
  display:block;
  color:var(--vx-navy);
  font-size:13px;
}

.vx-list-row p{
  margin:3px 0 0;
  color:var(--vx-muted);
  font-size:11px;
  line-height:1.4;
}

.vx-calm{
  margin-top:22px;
  padding:23px;
  border:1px solid #ECE4A7;
  border-radius:20px;
  background:#FFFBE7;
}

.vx-calm h3{
  margin:0 0 20px;
  color:var(--vx-navy);
  font-size:22px;
}

.vx-calm-grid{
  display:grid;
  grid-template-columns:repeat(5,1fr);
  gap:10px;
}

.vx-calm-item{
  min-height:150px;
  padding:10px 13px;
  text-align:center;
  border-right:1px solid #E8DEA6;
}

.vx-calm-item:last-child{border-right:0}
.vx-calm-item span{font-size:32px}

.vx-calm-item strong{
  display:block;
  margin:9px 0 5px;
  color:var(--vx-navy);
  font-size:13px;
}

.vx-calm-item p{
  margin:0;
  color:var(--vx-muted);
  font-size:11px;
  line-height:1.45;
}

/* ALIMENTACIÓN */

.vx-meals{
  display:grid;
  grid-template-columns:1fr 1fr;
  gap:15px;
}

.vx-meal{
  display:flex;
  align-items:center;
  gap:17px;
  padding:21px;
  border:1px solid var(--vx-line);
  border-radius:18px;
  background:var(--vx-cream);
}

.vx-meal-icon{
  width:55px;
  height:55px;
  display:grid;
  place-items:center;
  flex-shrink:0;
  border-radius:50%;
  background:white;
  font-size:29px;
}

.vx-meal strong{
  display:block;
  color:var(--vx-navy);
  font-size:21px;
}

.vx-meal small{
  display:block;
  margin-top:4px;
  color:var(--vx-muted);
}

.vx-feeding-steps{
  display:grid;
  grid-template-columns:repeat(4,1fr);
  gap:14px;
}

.vx-step{
  padding:19px;
  border:1px solid var(--vx-line);
  border-radius:18px;
  background:white;
  text-align:center;
}

.vx-step-number{
  width:30px;
  height:30px;
  display:grid;
  place-items:center;
  margin:auto;
  border-radius:50%;
  background:var(--vx-navy);
  color:white;
  font-size:12px;
  font-weight:900;
}

.vx-step-icon{
  margin:13px 0 7px;
  font-size:36px;
}

.vx-step p{
  margin:0;
  color:var(--vx-muted);
  font-size:12px;
  line-height:1.5;
}

.vx-warning{
  margin-top:20px;
  padding:18px 20px;
  border:1px solid #E8E0A0;
  border-radius:17px;
  background:var(--vx-yellow);
  color:var(--vx-navy);
  font-size:13px;
  line-height:1.55;
}

/* SALUD */

.vx-health-grid{
  display:grid;
  grid-template-columns:repeat(4,1fr);
  gap:15px;
}

.vx-health-card{
  padding:21px;
  border:1px solid var(--vx-line);
  border-radius:18px;
  background:white;
}

.vx-health-card .icon{font-size:29px}

.vx-health-card small{
  display:block;
  margin-top:12px;
  color:var(--vx-muted);
  font-size:10px;
  font-weight:900;
  text-transform:uppercase;
}

.vx-health-card strong{
  display:block;
  margin-top:7px;
  color:var(--vx-navy);
  font-size:13px;
  line-height:1.45;
}

.vx-table-wrap{
  width:100%;
  overflow-x:auto;
  margin-top:13px;
  border:1px solid var(--vx-line);
  border-radius:17px;
}

.vx-table{
  width:100%;
  min-width:670px;
  border-collapse:collapse;
  background:white;
}

.vx-table th,.vx-table td{
  padding:13px 14px;
  text-align:left;
  border-bottom:1px solid var(--vx-line);
  font-size:11px;
}

.vx-table th{
  background:var(--vx-soft);
  color:var(--vx-navy);
  font-weight:900;
}

.vx-table td{color:var(--vx-muted)}

/* RUTINA */

.vx-routine{
  display:grid;
  grid-template-columns:repeat(3,1fr);
  gap:15px;
}

.vx-routine-card{
  overflow:hidden;
  border:1px solid var(--vx-line);
  border-radius:20px;
  background:white;
}

.vx-routine-head{
  padding:16px 18px;
  background:var(--vx-cream);
  color:var(--vx-navy);
  font-size:16px;
  font-weight:900;
}

.vx-routine-body{padding:8px 18px}

.vx-routine-row{
  padding:12px 0;
  border-bottom:1px solid var(--vx-line);
  color:var(--vx-muted);
  font-size:13px;
}

.vx-routine-row:last-child{border-bottom:0}

.vx-note{
  margin-top:20px;
  padding:18px 20px;
  border-radius:17px;
  background:var(--vx-blue);
  color:var(--vx-navy);
  font-size:13px;
  line-height:1.7;
}

/* SEGURIDAD */

.vx-alert{
  margin-bottom:20px;
  padding:17px 20px;
  border:1px solid #F3CCCC;
  border-radius:18px;
  background:var(--vx-red);
  color:#A84242;
  font-size:13px;
  font-weight:900;
}

.vx-safety-grid{
  display:grid;
  grid-template-columns:repeat(3,1fr);
  gap:15px;
}

.vx-safety-card{
  min-height:190px;
  padding:21px;
  border:1px solid var(--vx-line);
  border-radius:20px;
  background:white;
}

.vx-safety-card .icon{font-size:31px}

.vx-safety-card h3{
  margin:12px 0 7px;
  color:var(--vx-navy);
  font-size:16px;
}

.vx-safety-card p{
  margin:0;
  color:var(--vx-muted);
  font-size:13px;
  line-height:1.55;
}

/* CONTACTOS */

.vx-contact-grid{
  display:grid;
  grid-template-columns:1fr 1fr;
  gap:18px;
}

.vx-contact-card{
  padding:24px;
  border:1px solid var(--vx-line);
  border-radius:20px;
  background:white;
  box-shadow:0 5px 20px rgba(8,44,76,.05);
}

.vx-contact-card.emergency{border-top:4px solid var(--vx-lime)}

.vx-contact-label{
  color:var(--vx-muted);
  font-size:10px;
  font-weight:900;
  text-transform:uppercase;
}

.vx-contact-card h3{
  margin:8px 0 12px;
  color:var(--vx-navy);
  font-size:21px;
}

.vx-contact-card p{
  margin:7px 0;
  color:var(--vx-muted);
  font-size:13px;
}

.vx-contact-buttons{
  display:flex;
  gap:10px;
  margin-top:18px;
}

.vx-contact-button{
  flex:1;
  padding:12px 10px;
  border-radius:10px;
  text-align:center;
  font-size:12px;
  font-weight:900;
}

.vx-contact-button.call{background:var(--vx-navy);color:white}
.vx-contact-button.whatsapp{background:var(--vx-lime);color:var(--vx-navy)}

.vx-qr-section{
  display:flex;
  align-items:center;
  justify-content:space-between;
  gap:25px;
  margin-top:20px;
  padding:24px;
  border:1px solid var(--vx-line);
  border-radius:20px;
  background:white;
}

.vx-qr-section h3{margin:0 0 8px;color:var(--vx-navy)}

.vx-qr-section p{
  max-width:570px;
  margin:0 0 12px;
  color:var(--vx-muted);
  font-size:13px;
  line-height:1.55;
}

.vx-url{
  display:inline-block;
  max-width:100%;
  padding:8px 10px;
  overflow:auto;
  border-radius:8px;
  background:var(--vx-soft);
  color:var(--vx-navy);
  font-size:11px;
}

/* FOOTER PROPIO */

.vx-footer{
  padding:30px 25px 35px;
  background:var(--vx-navy);
  color:white;
  text-align:center;
}

.vx-footer-paw{color:var(--vx-lime);font-size:34px}

.vx-footer strong{
  display:block;
  margin-top:5px;
  font-size:18px;
}

.vx-footer p{
  margin:7px 0 0;
  color:rgba(255,255,255,.75);
  font-size:11px;
}

.vx-mobile-nav{display:none}

/* TABLET */

@media(max-width:1050px){
  .vx-nav{display:none}
  .vx-feature-grid{grid-template-columns:repeat(2,1fr)}
  .vx-found{grid-template-columns:110px 1fr}
  .vx-found-photo{width:100px;height:100px}
  .vx-found-buttons{grid-column:1/-1}
  .vx-summary-grid{grid-template-columns:1fr 1fr}
  .vx-summary-card:last-child{grid-column:1/-1}
  .vx-personality,.vx-health-grid{grid-template-columns:repeat(2,1fr)}
  .vx-feeding-steps{grid-template-columns:1fr 1fr}
  .vx-safety-grid{grid-template-columns:1fr 1fr}

  .vx-mobile-nav{
    position:fixed;
    left:0;
    right:0;
    bottom:0;
    z-index:1000;
    display:grid;
    grid-template-columns:repeat(5,1fr);
    padding:6px 4px max(6px,env(safe-area-inset-bottom));
    border-top:1px solid var(--vx-line);
    background:rgba(255,255,255,.98);
    box-shadow:0 -5px 25px rgba(8,44,76,.08);
  }

  .vx-mobile-nav button{
    display:flex;
    flex-direction:column;
    align-items:center;
    gap:3px;
    padding:5px 2px;
    border:0;
    background:transparent;
    color:var(--vx-muted);
    font-size:17px;
  }

  .vx-mobile-nav button small{font-size:8px;font-weight:800}
  .vx-mobile-nav button.active{color:var(--vx-navy)}
  .vx-footer{padding-bottom:90px}
}

/* MÓVIL */

@media(max-width:700px){
  .vx-top{height:64px;padding:0 16px}
  .vx-brand-text strong{font-size:17px}
  .vx-brand-paw{width:34px;height:34px;font-size:26px}

  .vx-hero{
    min-height:555px;
    display:flex;
    flex-direction:column;
    justify-content:flex-end;
  }

  .vx-hero-photo-wrap{width:100%;height:100%}

  .vx-hero-photo-wrap::after{
    background:linear-gradient(0deg,rgba(8,44,76,.99) 0%,rgba(8,44,76,.88) 30%,rgba(8,44,76,.10) 70%);
  }

  .vx-hero-content{
    width:100%;
    padding:270px 22px 38px;
    text-align:center;
  }

  .vx-passport-pill{padding:8px 13px;font-size:10px}
  .vx-hero h1{margin-top:13px;font-size:56px}
  .vx-hero h2{font-size:25px}
  .vx-hero-message{margin:14px auto 0;font-size:14px}
  .vx-hero-tags{justify-content:center;margin-top:16px}

  .vx-identification{
    width:100%;
    grid-template-columns:1fr 1fr;
    margin:0;
    border-left:0;
    border-right:0;
    border-radius:0;
  }

  .vx-id-item{min-height:108px;border-bottom:1px solid var(--vx-line)}
  .vx-id-item:nth-child(2n){border-right:0}
  .vx-id-item:last-child{grid-column:1/-1;border-bottom:0}

  .vx-container,.vx-page{width:calc(100% - 28px)}
  .vx-home{padding-top:32px}
  .vx-feature-grid{grid-template-columns:1fr 1fr;gap:10px}
  .vx-feature-card{min-height:205px;padding:17px 12px}
  .vx-feature-icon{width:53px;height:53px;font-size:25px}
  .vx-feature-card h3{font-size:14px}
  .vx-feature-card p{font-size:11px}

  .vx-found{
    grid-template-columns:72px 1fr;
    gap:14px;
    padding:17px;
  }

  .vx-found-photo{width:70px;height:70px;border-width:3px}
  .vx-found-content h2{font-size:21px}
  .vx-found-content p{font-size:11px}

  .vx-found-buttons{
    grid-column:1/-1;
    grid-template-columns:1fr;
  }

  .vx-found-phone{font-size:16px}
  .vx-summary-grid{grid-template-columns:1fr}
  .vx-summary-card:last-child{grid-column:auto}
  .vx-quote{flex-direction:column;padding:20px}
  .vx-page{padding:24px 0 90px}
  .vx-page-title{align-items:flex-start}
  .vx-page-title-icon{width:52px;height:52px;font-size:25px}
  .vx-page-title h1{font-size:27px}
  .vx-personality{grid-template-columns:1fr 1fr;gap:10px}
  .vx-profile-lists{grid-template-columns:1fr}
  .vx-calm-grid{grid-template-columns:1fr}

  .vx-calm-item{
    min-height:auto;
    border-right:0;
    border-bottom:1px solid #E8DEA6;
  }

  .vx-meals{grid-template-columns:1fr}
  .vx-feeding-steps{grid-template-columns:1fr 1fr}
  .vx-health-grid{grid-template-columns:1fr 1fr}
  .vx-grid-2{grid-template-columns:1fr}
  .vx-routine{grid-template-columns:1fr}
  .vx-safety-grid{grid-template-columns:1fr}
  .vx-contact-grid{grid-template-columns:1fr}
  .vx-contact-buttons{flex-direction:column}
  .vx-qr-section{flex-direction:column;align-items:flex-start}
}

@media(max-width:420px){
  .vx-feature-grid{grid-template-columns:1fr}
  .vx-feature-card{min-height:auto}
  .vx-personality{grid-template-columns:1fr}
  .vx-health-grid{grid-template-columns:1fr}
  .vx-feeding-steps{grid-template-columns:1fr}
}
`;

/* ============================================================
   COMPONENTES
============================================================ */

function PageTitle({ icon, title, subtitle }) {
  return (
    <div className="vx-page-title">
      <div className="vx-page-title-icon">{icon}</div>
      <div>
        <h1>{title}</h1>
        <p>{subtitle}</p>
      </div>
    </div>
  );
}

function ContactCard({ person, emergency = false }) {
  const phone = person.phones[0];
  const message = encodeURIComponent(
    `Hola ${person.shortName}, encontré a Vulpix. Estoy viendo su pasaporte digital.`
  );

  return (
    <article className={`vx-contact-card ${emergency ? "emergency" : ""}`}>
      <div style={{ fontSize: 31 }}>{emergency ? "🚨" : "❤️"}</div>

      <div className="vx-contact-label">
        {emergency ? "Contacto de emergencia" : "Responsable"}
      </div>

      <h3>{person.name}</h3>
      <p>📍 {PET.city}</p>

      {person.phones.map((number) => (
        <p key={number}>📞 {number}</p>
      ))}

      <div className="vx-contact-buttons">
        <a className="vx-contact-button call" href={`tel:+57${phone}`}>
          📞 Llamar
        </a>

        <a
          className="vx-contact-button whatsapp"
          href={`https://wa.me/57${phone}?text=${message}`}
          target="_blank"
          rel="noreferrer"
        >
          💬 WhatsApp
        </a>
      </div>
    </article>
  );
}

/* ============================================================
   VULPIX
============================================================ */

function Vulpix() {
  const [page, setPage] = useState("inicio");

  const qrUrl = useMemo(() => {
    if (typeof window === "undefined") return "https://zaphy.com.co/vulpix";
    return `${window.location.origin}/vulpix`;
  }, []);

  const go = (nextPage) => {
    setPage(nextPage);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const nav = [
    ["inicio", "⌂", "INICIO"],
    ["perfil", "♥", "PERFIL"],
    ["alimentacion", "🍽️", "ALIMENTACIÓN"],
    ["salud", "✚", "SALUD"],
    ["rutina", "◷", "RUTINA"],
    ["seguridad", "🛡️", "SEGURIDAD"],
    ["contactos", "☎", "CONTACTOS"],
  ];

  /* ==========================================================
     INICIO
  ========================================================== */

  const renderHome = () => {
    const cards = [
      ["perfil", "♡", "Perfil y Personalidad", "Conoce cómo soy, mi comportamiento y lo que me hace feliz."],
      ["alimentacion", "🍽️", "Alimentación", "Horarios, cantidades y cómo preparar correctamente mi comida."],
      ["salud", "🩺", "Salud", "Información médica, vacunas, identificación y datos importantes."],
      ["rutina", "🕐", "Rutina", "Mis horarios de paseos, juegos, descanso y actividades."],
      ["seguridad", "🛡️", "Seguridad", "Recomendaciones importantes para mantenerme segura."],
      ["contactos", "☎️", "Mis Humanos", "Contactos de mi familia y personas autorizadas para cuidarme."],
    ];

    const jennyPhone = PET.responsible.phones[0];
    const andresPhone = PET.emergency.phones[0];

    return (
      <>
        <section className="vx-hero">
          <div className="vx-hero-photo-wrap">
            <img src={PHOTO_URL} alt="Vulpix" className="vx-hero-photo" />
          </div>

          <div className="vx-hero-content">
            <span className="vx-passport-pill">🐾 PASAPORTE DIGITAL</span>
            <h1>VULPIX</h1>
            <h2>Castañeda Ughetti</h2>
            <p className="vx-hero-message">Todo lo que necesitas saber para cuidarme bien. ❤️</p>

            <div className="vx-hero-tags">
              <span>🐶 Border Collie</span>
              <span>♀ Hembra</span>
              <span>💙 Ojos azules</span>
            </div>
          </div>
        </section>

        <section className="vx-identification">
          <div className="vx-id-item">
            <div className="vx-id-icon">📅</div>
            <small>Nacimiento</small>
            <strong>{PET.birth}</strong>
          </div>

          <div className="vx-id-item">
            <div className="vx-id-icon">🐕</div>
            <small>Raza</small>
            <strong>{PET.breed}</strong>
          </div>

          <div className="vx-id-item">
            <div className="vx-id-icon">♀️</div>
            <small>Sexo</small>
            <strong>{PET.sex}</strong>
          </div>

          <div className="vx-id-item">
            <div className="vx-id-icon">💛</div>
            <small>Características</small>
            <strong>{PET.eyes}<br />{PET.color}</strong>
          </div>

          <div className="vx-id-item">
            <div className="vx-id-icon">🆔</div>
            <small>Chip</small>
            <strong>{PET.chip}</strong>
          </div>
        </section>

        <div className="vx-container vx-home">
          <div className="vx-section-heading">
            <h2>🐾 Conoce a Vulpix</h2>
            <p>Explora las secciones de mi pasaporte digital.</p>
          </div>

          <div className="vx-feature-grid">
            {cards.map(([id, icon, title, text]) => (
              <button key={id} type="button" className="vx-feature-card" onClick={() => go(id)}>
                <div className="vx-feature-icon">{icon}</div>
                <h3>{title}</h3>
                <p>{text}</p>
                <b>Ver más</b>
              </button>
            ))}
          </div>

          <section className="vx-found">
            <img src={PHOTO_URL} alt="Vulpix" className="vx-found-photo" />

            <div className="vx-found-content">
              <h2>¿Me <span>encontraste?</span></h2>
              <p>
                Si me ves sola o crees que estoy perdida, por favor comunícate
                inmediatamente con mis humanos. ❤️
              </p>
            </div>

            <div className="vx-found-buttons">
              <a className="vx-found-button primary" href={`tel:+57${jennyPhone}`}>
                📞 LLAMAR A JENNY
                <span className="vx-found-phone">300 750 3796</span>
              </a>

              <a className="vx-found-button secondary" href={`tel:+57${andresPhone}`}>
                📞 LLAMAR A ANDRÉS
                <span className="vx-found-phone">323 400 0710</span>
              </a>
            </div>
          </section>

          <div className="vx-summary-grid">
            <article className="vx-summary-card">
              <h3>🐾 Sobre mí</h3>
              <p>
                Soy una perrita amorosa, inteligente y llena de energía.
                Necesito paciencia, comprensión y mucha seguridad.
              </p>

              <div className="vx-mini-list">
                <div className="vx-mini-row"><span>🎾 Personalidad</span><strong>Juguetona</strong></div>
                <div className="vx-mini-row"><span>💡 Aprendizaje</span><strong>Muy rápido</strong></div>
                <div className="vx-mini-row"><span>❤️ Familia</span><strong>Muy leal</strong></div>
                <div className="vx-mini-row"><span>🛡️ Necesito</span><strong>Rutina y seguridad</strong></div>
              </div>
            </article>

            <article className="vx-summary-card">
              <h3>🐾 Información rápida</h3>

              <div className="vx-mini-list">
                <div className="vx-mini-row"><span>🆔 Chip</span><strong>{PET.chip}</strong></div>
                <div className="vx-mini-row"><span>💉 Última vacuna</span><strong>{PET.vaccines}</strong></div>
                <div className="vx-mini-row"><span>🦠 Desparasitación</span><strong>{PET.deworming}</strong></div>
                <div className="vx-mini-row"><span>♀ Esterilizada</span><strong>{PET.sterilized}</strong></div>
                <div className="vx-mini-row"><span>🎨 Color</span><strong>Blanco y negro</strong></div>
                <div className="vx-mini-row"><span>👁️ Ojos</span><strong>{PET.eyes}</strong></div>
              </div>
            </article>

            <article className="vx-summary-card vx-qr-home">
              <h3>🐾 QR de mi pasaporte</h3>
              <p>Escanea este código para acceder a toda mi información.</p>

              <div className="vx-qr-box">
                <QRCodeSVG value={qrUrl} size={170} level="H" includeMargin />
              </div>
            </article>
          </div>

          <div className="vx-quote">
            <div className="vx-quote-paw">🐾</div>
            <div>
              <em>Soy Vulpix, una compañera única e incondicional.</em>
              <strong>Gracias por cuidarme y amarme. ❤️</strong>
            </div>
          </div>
        </div>
      </>
    );
  };

  /* ==========================================================
     PERFIL
  ========================================================== */

  const renderProfile = () => (
    <div className="vx-page">
      <button className="vx-back" onClick={() => go("inicio")}>← Volver al inicio</button>

      <PageTitle
        icon="♡"
        title="Perfil y personalidad"
        subtitle="Conoce cómo soy, mi comportamiento y lo que me hace feliz. ❤️"
      />

      <h2 className="vx-subtitle">🐾 Mi personalidad</h2>

      <div className="vx-personality">
        {PET.personality.map((item) => (
          <article className="vx-personality-card" key={item.title}>
            <div className="icon">{item.icon}</div>
            <h3>{item.title}</h3>
            <p>{item.text}</p>
          </article>
        ))}
      </div>

      <h2 className="vx-subtitle">🐾 Socialización</h2>

      <div className="vx-grid-2">
        <div className="vx-box" style={{ background: "#F7FAF0", borderColor: "#CDDFA5" }}>
          <h3>♀️ 💚 Me llevo mejor con hembras</h3>
          <p>Disfruto mucho la compañía y el juego con otras perritas.</p>
        </div>

        <div className="vx-box" style={{ background: "#FFF5F5", borderColor: "#EDCACA" }}>
          <h3>🚫 Evito machos</h3>
          <p>No me siento cómoda con perros machos. Por favor evita que interactúe con ellos.</p>
        </div>
      </div>

      <div className="vx-profile-lists">
        <div className="vx-list-box likes">
          <h3>LE ENCANTA</h3>

          {PET.likes.map((item) => (
            <div className="vx-list-row" key={item.title}>
              <div className="vx-list-row-icon">{item.icon}</div>
              <div>
                <strong>{item.title}</strong>
                <p>{item.text}</p>
              </div>
            </div>
          ))}
        </div>

        <div className="vx-list-box avoid">
          <h3>NO LE GUSTA</h3>

          {PET.avoid.map((item) => (
            <div className="vx-list-row" key={item.title}>
              <div className="vx-list-row-icon">{item.icon}</div>
              <div>
                <strong>{item.title}</strong>
                <p>{item.text}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="vx-calm">
        <h3>❤️ Cuando estoy nerviosa</h3>

        <div className="vx-calm-grid">
          {PET.calm.map((item) => (
            <div className="vx-calm-item" key={item.title}>
              <span>{item.icon}</span>
              <strong>{item.title}</strong>
              <p>{item.text}</p>
            </div>
          ))}
        </div>
      </div>

      <div className="vx-note">
        🛡️ <strong>Recuerda:</strong> aprender a conocerme y respetar mis necesidades
        hará que nuestra convivencia sea feliz y armoniosa. 🐾
      </div>
    </div>
  );

  /* ==========================================================
     ALIMENTACIÓN
  ========================================================== */

  const renderFood = () => (
    <div className="vx-page">
      <button className="vx-back" onClick={() => go("inicio")}>← Volver al inicio</button>

      <PageTitle icon="🍽️" title="Alimentación" subtitle="Mi comida, horarios y preparación." />

      <h2 className="vx-subtitle">🕐 Mis horarios</h2>

      <div className="vx-meals">
        {PET.meals.map((meal) => (
          <article className="vx-meal" key={meal.time}>
            <div className="vx-meal-icon">🕐</div>
            <div>
              <strong>{meal.time}</strong>
              <small>{meal.label}</small>
            </div>
          </article>
        ))}
      </div>

      <h2 className="vx-subtitle">🐾 Preparación de mi comida</h2>

      <div className="vx-feeding-steps">
        {PET.feeding.map((step, index) => (
          <article className="vx-step" key={step}>
            <div className="vx-step-number">{index + 1}</div>
            <div className="vx-step-icon">{["🥄", "☕", "🥣", "🥄"][index]}</div>
            <p>{step}</p>
          </article>
        ))}
      </div>

      <h2 className="vx-subtitle">🐕 ¿Cómo se divide la preparación?</h2>

      <div className="vx-grid-2">
        <div className="vx-box">
          <h3>🐕 Para Eevee</h3>
          <p>3 cucharadas soperas de la preparación.</p>
        </div>

        <div className="vx-box">
          <h3>🐕 Para Vulpix</h3>
          <p>El resto de la preparación.</p>
        </div>
      </div>

      <div className="vx-warning">
        ❤️ <strong>Importante:</strong> respetar las cantidades y horarios establecidos
        por la familia. No modificar mi alimentación sin autorización.
      </div>
    </div>
  );

  /* ==========================================================
     SALUD
  ========================================================== */

  const renderHealth = () => (
    <div className="vx-page">
      <button className="vx-back" onClick={() => go("inicio")}>← Volver al inicio</button>

      <PageTitle
        icon="🩺"
        title="Salud e identificación"
        subtitle="Mi información médica más importante."
      />

      <div className="vx-health-grid">
        <article className="vx-health-card">
          <div className="icon">🆔</div>
          <small>Chip de identificación</small>
          <strong>{PET.chip}</strong>
        </article>

        <article className="vx-health-card">
          <div className="icon">💉</div>
          <small>Vacunas</small>
          <strong>Última actualización<br />{PET.vaccines}</strong>
        </article>

        <article className="vx-health-card">
          <div className="icon">📅</div>
          <small>Nacimiento</small>
          <strong>{PET.birth}</strong>
        </article>

        <article className="vx-health-card">
          <div className="icon">🐾</div>
          <small>Raza / Sexo</small>
          <strong>{PET.breed}<br />{PET.sex}</strong>
        </article>
      </div>

      <h2 className="vx-subtitle">💉 Historial de vacunas</h2>

      <div className="vx-table-wrap">
        <table className="vx-table">
          <thead>
            <tr>
              <th>Vacuna</th>
              <th>Fecha aplicada</th>
              <th>Próxima fecha</th>
              <th>Veterinario</th>
              <th>Observaciones</th>
            </tr>
          </thead>

          <tbody>
            <tr>
              <td>Última actualización general</td>
              <td>{PET.vaccines}</td>
              <td>—</td>
              <td>—</td>
              <td>—</td>
            </tr>
          </tbody>
        </table>
      </div>

      <div className="vx-grid-2" style={{ marginTop: 22 }}>
        <article className="vx-box">
          <h3>🔎 Información adicional</h3>
          <p>🎨 Color: {PET.color}</p>
          <p>👁️ Ojos: {PET.eyes}</p>
          <p>♀ Esterilizada: {PET.sterilized}</p>
          <p>🦠 Desparasitación: {PET.deworming}</p>
          <p>📍 Ciudad: {PET.city}</p>
        </article>

        <article className="vx-box" style={{ background: "#FFF5F5" }}>
          <h3>📝 Información por completar</h3>
          <p>• Veterinario habitual</p>
          <p>• Clínica / teléfono</p>
          <p>• Alergias conocidas</p>
          <p>• Medicamentos actuales</p>
          <p>• Próximas vacunas</p>
        </article>
      </div>
    </div>
  );

  /* ==========================================================
     RUTINA
  ========================================================== */

  const renderRoutine = () => {
    const sections = [
      ["🌅 Mañana", PET.routine.morning],
      ["☀️ Tarde", PET.routine.afternoon],
      ["🌙 Noche", PET.routine.night],
    ];

    return (
      <div className="vx-page">
        <button className="vx-back" onClick={() => go("inicio")}>← Volver al inicio</button>

        <PageTitle icon="🕐" title="Rutina diaria" subtitle="Mis actividades y horarios del día." />

        <div className="vx-note" style={{ marginTop: 0, marginBottom: 20 }}>
          <strong>Esta rutina puede variar según el día.</strong><br />
          Siempre verificar con mi familia si hay cambios.
        </div>

        <div className="vx-routine">
          {sections.map(([title, rows]) => (
            <article className="vx-routine-card" key={title}>
              <div className="vx-routine-head">{title}</div>

              <div className="vx-routine-body">
                {rows.map((row) => (
                  <div className="vx-routine-row" key={row}>{row}</div>
                ))}
              </div>
            </article>
          ))}
        </div>

        <div className="vx-note">
          <strong>🐾 Notas importantes</strong><br />
          • Necesito actividad física diaria para gastar energía.<br />
          • Me encanta correr y perseguir pelotas.<br />
          • Después de jugar necesito agua fresca.<br />
          • Me gusta dormir en un lugar tranquilo y seguro.
        </div>
      </div>
    );
  };

  /* ==========================================================
     SEGURIDAD
  ========================================================== */

  const renderSafety = () => {
    const items = [
      ["🚪", "Puertas y portones", "Antes de abrir una puerta, portón o reja, verifica siempre dónde estoy."],
      ["🦮", "Salidas", "Revisa que mi collar o arnés esté puesto. Supervisa entradas y salidas del hogar."],
      ["😰", "Si me asusto", "No me persigas ni me acorrales. Háblame con calma y dame espacio."],
      ["🐕", "Interacción con otros perros", "Me llevo mejor con hembras. Evita que interactúe con perros machos."],
      ["🌿", "Entorno seguro", "Evita dejarme sola en lugares desde donde pueda escapar."],
      ["👀", "Supervisión", "Cuando haya visitas o ruidos fuertes, acompáñame y tranquilízame."],
    ];

    return (
      <div className="vx-page">
        <button className="vx-back" onClick={() => go("inicio")}>← Volver al inicio</button>

        <PageTitle
          icon="🛡️"
          title="Seguridad y cuidados"
          subtitle="Recomendaciones para mantenerme segura."
        />

        <div className="vx-alert">
          ⚠️ Soy nerviosa y escapista. Por favor sigue estas recomendaciones siempre.
        </div>

        <div className="vx-safety-grid">
          {items.map(([icon, title, text]) => (
            <article className="vx-safety-card" key={title}>
              <div className="icon">{icon}</div>
              <h3>{title}</h3>
              <p>{text}</p>
            </article>
          ))}
        </div>

        <div className="vx-warning">
          💚 <strong>Regla de oro:</strong> tu calma me ayuda a sentirme segura.
          Gracias por cuidarme.
        </div>
      </div>
    );
  };

  /* ==========================================================
     CONTACTOS
  ========================================================== */

  const renderContacts = () => (
    <div className="vx-page">
      <button className="vx-back" onClick={() => go("inicio")}>← Volver al inicio</button>

      <PageTitle
        icon="🐾"
        title="Mis humanos"
        subtitle="Contactos de mi familia y personas autorizadas."
      />

      <div className="vx-contact-grid">
        <ContactCard person={PET.responsible} />
        <ContactCard person={PET.emergency} emergency />
      </div>

      <article className="vx-box" style={{ marginTop: 20, background: "#FFFDF3" }}>
        <h3>📝 Información adicional</h3>
        <p>• Veterinario habitual: ______________________________</p>
        <p>• Clínica / Teléfono: ______________________________</p>
        <p>• Notas adicionales: ______________________________</p>
      </article>

      <section className="vx-qr-section">
        <div>
          <h3>🪪 QR de mi pasaporte</h3>
          <p>
            Este código QR lleva directamente al pasaporte digital de Vulpix
            y es el que utilizaremos en su carnet físico.
          </p>
          <code className="vx-url">{qrUrl}</code>
        </div>

        <div className="vx-qr-box">
          <QRCodeSVG value={qrUrl} size={180} level="H" includeMargin />
        </div>
      </section>
    </div>
  );

  /* ==========================================================
     SELECCIÓN
  ========================================================== */

  const renderPage = () => {
    switch (page) {
      case "perfil": return renderProfile();
      case "alimentacion": return renderFood();
      case "salud": return renderHealth();
      case "rutina": return renderRoutine();
      case "seguridad": return renderSafety();
      case "contactos": return renderContacts();
      default: return renderHome();
    }
  };

  return (
    <div className="vx-app">
      <style>{CSS}</style>

      <header className="vx-top">
        <button type="button" className="vx-brand" onClick={() => go("inicio")}>
          <span className="vx-brand-paw">🐾</span>
          <span className="vx-brand-text">
            <strong>VULPIX</strong>
            <small>Pasaporte Digital</small>
          </span>
        </button>

        <nav className="vx-nav">
          {nav.map(([id, icon, label]) => (
            <button
              type="button"
              key={id}
              className={page === id ? "active" : ""}
              onClick={() => go(id)}
            >
              <span className="icon">{icon}</span>
              <span>{label}</span>
            </button>
          ))}
        </nav>
      </header>

      <main>{renderPage()}</main>

      <footer className="vx-footer">
        <div className="vx-footer-paw">🐾</div>
        <strong>Vulpix Castañeda Ughetti</strong>
        <p>Pasaporte Digital · Identificación · Cuidados · Seguridad</p>
        <p>Hecho con amor por mi familia ❤️</p>
      </footer>

      <nav className="vx-mobile-nav">
        {[
          ["inicio", "⌂", "Inicio"],
          ["perfil", "♡", "Perfil"],
          ["alimentacion", "🍽️", "Comida"],
          ["salud", "✚", "Salud"],
          ["contactos", "☎", "Contacto"],
        ].map(([id, icon, label]) => (
          <button
            type="button"
            key={id}
            className={page === id ? "active" : ""}
            onClick={() => go(id)}
          >
            <span>{icon}</span>
            <small>{label}</small>
          </button>
        ))}
      </nav>
    </div>
  );
}

export default Vulpix;